// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import { type ObjectMode } from '@databases/types';
import { type OptionalCreatePageReference } from '@databases/types/confluence';
import {
  BacklinkDisplayType,
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  type DatabaseWithoutEntries,
  type EntryWithContext
} from '@databases/types/database';

import { isPageReference } from '../confluence/utils';

import { convertType, getStringRepresentation } from './field-types';
import { normalizePageLinkValue } from './page-link-utils';
import { getDatabaseValue, getFirstField } from './utils';

/**
 * Cache for field conversion results to ensure stable `_id` property values.
 * Cache key format: "sourceFieldType->targetFieldType:serialized_value"
 * Example: "text->jira:\"CTD-4786\""
 */
type ConversionCacheKey = `${string}->${string}:${string}`;
export const conversionCache = new Map<ConversionCacheKey, DatabaseValue>();

/**
 * Returns the database value that belongs to the specified field and entry. If the entry has no value for the
 * specified field, the default value of the field is returned. If the value has a different type than the field,
 * it is converted to the field type. If the conversion is not possible, an empty value is returned.
 */
export function getConvertedDatabaseValue<Type extends DatabaseFieldType>(
  database: DatabaseWithoutEntries<ObjectMode>,
  field: DatabaseField<Type>,
  entry: DatabaseEntry,
  state: DefaultRootState | undefined,
  recursionTracker: { recursionCount: number } = { recursionCount: 0 }
): DatabaseValue<Type> {
  const rawValue = getDatabaseValue(database, field, entry);

  if (rawValue.type === field.type) {
    return rawValue as DatabaseValue<Type>;
  }

  const cacheKey: ConversionCacheKey = `${rawValue.type}->${field.type}:${JSON.stringify(
    rawValue.value
  )}`;

  if (conversionCache.has(cacheKey)) {
    return conversionCache.get(cacheKey) as DatabaseValue<Type>;
  }

  const convertedValue = convertType(
    { database, field, entry, value: rawValue },
    field.type,
    state,
    undefined,
    recursionTracker
  );

  conversionCache.set(cacheKey, convertedValue);

  return convertedValue;
}

/**
 * Returns the first field value of the specified database entry as a string.
 * If the value is empty, undefined is returned and it's up to the caller
 * to display a localized fallback of the "Unnamed entry" string.
 */
export function getEntryName(
  { database, entry }: EntryWithContext,
  state: DefaultRootState | undefined,
  recursionTracker = { recursionCount: 0 }
): string | undefined {
  const field = getFirstField(database);

  const fieldRepresentation = field
    ? getStringRepresentation(
        { database, field, entry, value: getDatabaseValue(database, field, entry) },
        state,
        { recursionCount: recursionTracker.recursionCount }
      )
    : undefined;

  if (
    field?.type === DatabaseFieldType.ENTRY_LINK &&
    fieldRepresentation?.startsWith('Unnamed entry:')
  ) {
    return '';
  }

  return fieldRepresentation;
}

export function getNumberRepresentation(
  database: Database,
  field: DatabaseField,
  entry: DatabaseEntry,
  value: DatabaseValue,
  state: DefaultRootState | undefined
) {
  const fieldWithDisplayAsNumber = {
    ...field,
    settings: {
      ...field.settings,
      backlinkDisplayType: BacklinkDisplayType.NUMBER
    }
  };
  return getStringRepresentation(
    { database, field: fieldWithDisplayAsNumber, entry, value },
    state
  );
}

export function getEntryPageReference(
  database: Database<ObjectMode>,
  entry: DatabaseEntry,
  state: DefaultRootState | undefined
): OptionalCreatePageReference | undefined {
  const firstField = getFirstField(database);
  const firstFieldValue =
    firstField?.type === DatabaseFieldType.PAGE_LINK
      ? normalizePageLinkValue(
          getConvertedDatabaseValue<DatabaseFieldType.PAGE_LINK>(
            database,
            firstField as DatabaseField<DatabaseFieldType.PAGE_LINK>,
            entry,
            state
          ).value,
          state
        )
      : undefined;
  return firstFieldValue &&
    firstFieldValue.length > 0 &&
    isPageReference(firstFieldValue[0])
    ? firstFieldValue[0]
    : undefined;
}
